import { Account, AccountUser, Club, type ClubPreferences } from "@mixitone/models";
import { List } from "@mixitone/oom";
import { clubCountryForAccount } from "@mixitone/util";
import { RoutedLink } from "components/common/Link";
import { FormController } from "components/Forms/FormController";
import { AppController } from "controllers/AppController";
import type { FormFieldConfig } from "./FormFieldsDefinition";
import WarnIcon from "@mixitone/icons/warn.svg";
import ClubDirectoryListingActions from "components/Forms/ClubDirectoryListingActions";
import { directoryBaseUrlFromEnv } from "lib/directoryUrls";

export class ClubFormController extends FormController<
  Club,
  object,
  {
    contacts: List<AccountUser>;
    preferences: ClubPreferences;
  }
> {
  override async buildRecord(): Promise<Club> {
    const account = Account.current;
    return new Club({
      account_id: account?.id ?? "",
      country: account ? clubCountryForAccount(account) : "Other",
    });
  }
  override findModel(id: string) {
    return Club.query().find(id);
  }

  override async onLoad() {
    const club = this.state.record;
    const contacts = await AccountUser.query().eq("account_id", club.account_id).orderBy("name").all();

    if (this.state.newRecord) {
      club.contact_id = AccountUser.current?.uniqueId;
    }

    this.syncCountryFromAccount(club);

    this.setState({
      contacts: new List(...contacts),
      preferences: club.preferences || {},
    });
  }

  override async onDelete(): Promise<void> {
    try {
      if (this.get(AppController).state.currentClub?.id === this.state.record.id) {
        await this.get(AppController).actionSetCurrentClub(null, false);
      }
    } catch (error) {
      console.error("Error clearing current club:", error);
    }
  }

  override async saveRecord(club: Club) {
    this.syncCountryFromAccount(club);
    club.preferences = this.state.preferences;
    return club.save();
  }

  private syncCountryFromAccount(club: Club) {
    const account = Account.current;
    if (!account) return;

    const country = clubCountryForAccount(account);
    club.country = country;
    if (country === "Other") {
      if (club.listed_in_directory) {
        club.listed_in_directory = false;
      }
      club.region = undefined;
    }
  }

  fields() {
    const hasMemberPlayersFeature = Account.current?.hasFeature("member_players");
    const fields = {
      name: {
        name: "name",
        type: "text",
        label: "Name",
        required: true,
        group: "basic",
      },
      address: {
        name: "address",
        type: "text",
        label: "Address",
        inputType: "textarea",
        group: "public_directory",
      },
      contact_id: {
        name: "contact_id",
        type: "select",
        label: "Contact",
        options: this.state.contacts.map((c) => ({ value: c.id!, label: c.name! })).filter((c) => c.label),
        group: "basic",
      },
      casual_players: {
        name: "casual_players",
        type: "checkbox",
        label: "Allow casual participants",
        description: (
          <>
            Include drop-ins and guests in the{" "}
            <RoutedLink kind="link" to="/clubs/:clubId/players" params={{ clubId: this.state.record.id! }}>
              member book
            </RoutedLink>{" "}
            without formal membership requirements
          </>
        ),
        group: "players",
        value: () => this.state.record.getPreference("hasCasual", true) || !hasMemberPlayersFeature,
        onChange: (value) => {
          this.state.record.setPreference("hasCasual", value);
        },
        disabled: !hasMemberPlayersFeature,
      },
      member_players: {
        name: "member_players",
        type: "checkbox",
        label: "Track formal membership",
        description: (
          <>
            Apply membership requirements and documents to formal{" "}
            <RoutedLink kind="link" to="/clubs/:clubId/players" params={{ clubId: this.state.record.id! }}>
              members
            </RoutedLink>
          </>
        ),
        group: "players",
        value: () => hasMemberPlayersFeature && this.state.record.getPreference("hasMembers"),
        onChange: (value) => {
          this.state.record.setPreference("hasMembers", value);
        },
        disabled: !hasMemberPlayersFeature,
      },
      sport_type: {
        name: "sport_type",
        type: "select",
        label: "Sport Type",
        required: true,
        group: "public_directory",
        options: [
          { value: "badminton", label: "Badminton" },
          { value: "tennis", label: "Tennis" },
          { value: "pickleball", label: "Pickleball" },
          { value: "squash", label: "Squash" },
          { value: "other", label: "Other" },
        ],
      },
      listed_in_directory: {
        name: "listed_in_directory",
        type: "checkbox",
        label: "List in public directory",
        description:
          this.state.record.country === "Other" ? (
            <div className="p-3 mt-2 bg-yellow-100 border border-yellow-600 rounded-md">
              <div className="flex items-start">
                <WarnIcon className="mr-2 mt-0.5 h-5 w-5 shrink-0 fill-yellow-600" />
                <p className="text-sm text-yellow-800">
                  Clubs outside New Zealand cannot be listed in the public directory at this time.
                </p>
              </div>
            </div>
          ) : (
            <div className="mt-2 space-y-2">
              <p className="text-sm text-gray-600">
                Make this club visible in the{" "}
                <a
                  href={directoryBaseUrlFromEnv()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  public club directory
                </a>
                .
              </p>
              {this.state.record.listed_in_directory && this.state.record.id && (
                <ClubDirectoryListingActions
                  club={this.state.record}
                  onPrintPaletteChange={async (id) => {
                    const nextPrefs = { ...this.state.preferences, printPalette: id };
                    this.state.record.preferences = nextPrefs;
                    this.setState({ preferences: nextPrefs });
                    await this.state.record.save();
                  }}
                />
              )}
            </div>
          ),
        disabled: this.state.record.country === "Other",
        group: "public_directory",
      },
      enable_directory_contact: {
        name: "enable_directory_contact",
        type: "checkbox",
        label: "Enable contact form on directory listing",
        description:
          "Allow visitors to contact you through a form on your club's directory page. Messages will be sent to your contact email address.",
        disabled: !this.state.record.listed_in_directory || this.state.record.country === "Other",
        group: "public_directory",
      },
      publish_session_stats_in_directory: {
        name: "publish_session_stats_in_directory",
        type: "checkbox",
        label: "Show last session stats in public directory",
        description: "Shows anonymous attendance totals from the latest completed listed session.",
        disabled: !this.state.record.listed_in_directory || this.state.record.country === "Other",
        group: "public_directory",
      },
      country: {
        name: "country",
        type: "select",
        label: "Country",
        group: "public_directory",
        description: "Matches your account country.",
        disabled: true,
        options: [
          { value: "New Zealand", label: "New Zealand" },
          { value: "Other", label: "Other" },
        ],
      },
      region: {
        name: "region",
        type: "select",
        label: "Region",
        group: "public_directory",
        disabled: this.state.record.country !== "New Zealand",
        options: [
          { value: "Auckland", label: "Auckland" },
          { value: "Bay of Plenty", label: "Bay of Plenty" },
          { value: "Canterbury", label: "Canterbury" },
          { value: "Gisborne", label: "Gisborne" },
          { value: "Hawke's Bay", label: "Hawke's Bay" },
          { value: "Manawatu-Wanganui", label: "Manawatu-Wanganui" },
          { value: "Marlborough", label: "Marlborough" },
          { value: "Nelson", label: "Nelson" },
          { value: "Northland", label: "Northland" },
          { value: "Otago", label: "Otago" },
          { value: "Southland", label: "Southland" },
          { value: "Taranaki", label: "Taranaki" },
          { value: "Tasman", label: "Tasman" },
          { value: "Waikato", label: "Waikato" },
          { value: "Wairarapa", label: "Wairarapa" },
          { value: "Wellington", label: "Wellington" },
          { value: "West Coast", label: "West Coast" },
        ],
      },
      telephone: {
        name: "telephone",
        type: "text",
        label: "Telephone",
        inputType: "tel",
        group: "public_directory",
      },
      email: {
        name: "email",
        type: "text",
        label: "Email",
        inputType: "email",
        description:
          "Consider enabling the contact form instead of entering your email address, for spam protection.",
        group: "public_directory",
      },
      facebook_page: {
        name: "facebook_page",
        type: "text",
        label: "Facebook Page",
        inputType: "url",
        group: "public_directory",
      },
      webpage: {
        name: "webpage",
        type: "text",
        label: "Website",
        inputType: "url",
        group: "public_directory",
      },
    } as Record<string, FormFieldConfig>;

    if (hasMemberPlayersFeature && this.state.record.getPreference("hasMembers")) {
      fields.membership_requirements = {
        name: "membership_requirements",
        type: "fieldset",
        label: "Membership requirements",
        description: (
          <>
            You can also add relevant documents in the{" "}
            <RoutedLink kind="link" to="/clubs/:clubId/documents" params={{ clubId: this.state.record.id! }}>
              Documents tab
            </RoutedLink>{" "}
            that new members will need to review and agree to as part of their membership.
          </>
        ),
        group: "players",
        fields: [
          {
            name: "require_email",
            type: "checkbox",
            label: "Require email address",
            value: () => this.state.record.getMemberRequirement("email", false) ?? false,
            onChange: (value) => {
              this.state.record.setMemberRequirement("email", value);
            },
          },
          {
            name: "require_phone",
            type: "checkbox",
            label: "Require phone number",
            value: () => this.state.record.getMemberRequirement("phone", false) ?? false,
            onChange: (value) => {
              this.state.record.setMemberRequirement("phone", value);
            },
          },
        ],
      };
    }
    return fields;
  }
}
